// client/src/pages/secure/HotLeadsPage.js — Hot leads: who is most active on the website now,
// and (managing broker) the points, decay, thresholds and switches behind it.
//
// Agents see their own people; the managing broker, office manager and office staff see
// everyone (server-side scope, routes/hotLeads.js). Scores use website behaviour only.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { PageHeader, Panel, Tabs, Table, Input, Button, Notice, Loading } from '../../components/secure/pm/PMKit';
import { TierBadge } from '../../components/secure/HotLeadPanel';

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const LeadList = ({ seesEveryone }) => {
    const api = useApiClient();
    const [tier, setTier] = useState('hot');
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        setRows(null);
        api.get(`/api/hot-leads/list?tier=${tier}`).then((r) => setRows(r.rows)).catch((e) => setError(e.message));
    }, [api, tier]);
    const link = (r) => (r.crm_contact_id ? `/dashboard/crm/contacts/${r.crm_contact_id}` : r.client_id ? `/dashboard/clients/${r.client_id}` : null);
    return (
        <Panel title={seesEveryone ? 'All hot leads' : 'My hot leads'}
            subtitle="People who identified themselves and are active on the website now. Open one to see the full website timeline."
            actions={(
                <div className="flex gap-1">
                    {[['hot', 'Hot'], ['warm', 'Warm'], ['all', 'Hot + Warm']].map(([k, l]) => (
                        <Button key={k} variant={tier === k ? 'primary' : 'ghost'} onClick={() => setTier(k)}>{l}</Button>
                    ))}
                </div>
            )}>
            {error && <Notice message={error} />}
            {!rows ? <Loading /> : (
                <Table rows={rows.map((r) => ({ ...r, _key: `${r.subject_type}${r.subject_id}` }))} empty="Nobody in this band right now."
                    columns={[
                        { key: 'tier', label: 'Score', nowrap: true, render: (r) => <TierBadge tier={r.tier} score={Number(r.score)} /> },
                        { key: 'name', label: 'Who', render: (r) => (link(r) ? <Link className="text-indigo-700 hover:underline font-medium" to={link(r)}>{r.name || 'Unnamed'}</Link> : r.name) },
                        { key: 'reason', label: 'Why', render: (r) => <span className="text-slate-700">{r.reason ? `${cap(r.reason)}.` : '—'}</span> },
                        { key: 'agent_name', label: 'Agent', render: (r) => r.agent_name || <span className="text-slate-400">unassigned</span> },
                        { key: 'last_event_at', label: 'Last active', nowrap: true, render: (r) => fmt(r.last_event_at) },
                        { key: 'last_alert_at', label: 'Alerted', nowrap: true, render: (r) => fmt(r.last_alert_at) },
                    ]} />
            )}
        </Panel>
    );
};

const Toggle = ({ label, hint, checked, onChange, disabled }) => (
    <label className="flex items-start gap-3 py-2">
        <input type="checkbox" className="mt-1 h-4 w-4" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked ? 1 : 0)} />
        <span><span className="text-sm font-medium text-slate-800">{label}</span>{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
    </label>
);

const SettingsForm = ({ meta }) => {
    const api = useApiClient();
    const [s, setS] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => api.get('/api/hot-leads/settings').then(setS).catch((e) => setNotice({ tone: 'bad', message: e.message })), [api]);
    useEffect(() => { load(); }, [load]);
    if (!s) return notice ? <Notice {...notice} /> : <Loading />;
    const ro = !meta.can_edit_settings;
    const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
    const setW = (k) => (e) => setS((x) => ({ ...x, weights: { ...x.weights, [k]: e.target.value } }));
    const save = async (e) => {
        e.preventDefault();
        setBusy(true); setNotice(null);
        try {
            const body = { tracking_enabled: s.tracking_enabled, scoring_enabled: s.scoring_enabled, alerts_enabled: s.alerts_enabled,
                alert_email: s.alert_email, alert_sms: s.alert_sms, half_life_days: Number(s.half_life_days), window_days: Number(s.window_days),
                warm_threshold: Number(s.warm_threshold), hot_threshold: Number(s.hot_threshold), alert_cooldown_hours: Number(s.alert_cooldown_hours),
                daily_view_cap: Number(s.daily_view_cap), share_link_days: Number(s.share_link_days || 7), weights: Object.fromEntries(Object.entries(s.weights).map(([k, v]) => [k, Number(v)])) };
            const r = await api.put('/api/hot-leads/settings', body);
            setS(r);
            setNotice({ tone: 'good', message: `Saved. ${r.recomputed || 0} score(s) recomputed with the new settings (no alerts are sent by a settings change).` });
        } catch (err) { setNotice({ tone: 'bad', message: err.message, problems: err.problems }); } finally { setBusy(false); }
    };
    const resetDefaults = () => setS((x) => ({ ...x, ...meta.defaults, weights: Object.fromEntries(meta.event_types.map((t) => [t.key, t.default_weight])) }));
    return (
        <form onSubmit={save} className="space-y-6">
            {notice && <Notice {...notice} onDismiss={() => setNotice(null)} />}
            {ro && <Notice tone="info" message="Only the managing broker can change these. Shown so you can see how the score works." />}
            <Panel title="Switches">
                <Toggle label="Record website activity" checked={s.tracking_enabled} onChange={set('tracking_enabled')} disabled={ro}
                    hint="First-party only, one cookie. Browsers sending Do Not Track or Global Privacy Control are never recorded." />
                <Toggle label="Score leads" checked={s.scoring_enabled} onChange={set('scoring_enabled')} disabled={ro} />
                <Toggle label="Alert the agent in the portal when someone turns Hot" checked={s.alerts_enabled} onChange={set('alerts_enabled')} disabled={ro}
                    hint="Opens a task on the Tasks page and a note on the person's timeline, for the assigned agent (or the routing rule's agent, or the managing broker)." />
                <Toggle label="Also email the agent" checked={s.alert_email} onChange={set('alert_email')} disabled={ro}
                    hint="Through the outbound sending module: its auto-send switch, approval queue and compliance checks still apply. Off by default." />
                <Toggle label="Also text the agent" checked={s.alert_sms} onChange={set('alert_sms')} disabled={ro}
                    hint="To the mobile number in the agent's My Website Leads settings, through the outbound module. Off by default." />
            </Panel>
            <Panel title="Points per action" subtitle="Website behaviour only. The score never uses who a person is or where a property is (fair housing): those inputs are refused.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    {meta.event_types.map((t) => (
                        <Input key={t.key} type="number" min="0" max="100" step="0.5" label={t.label} value={s.weights[t.key] ?? ''} onChange={setW(t.key)} disabled={ro}
                            hint={`Default ${t.default_weight}${t.daily_cap === 'setting' ? '' : t.daily_cap ? ` · counted ${t.daily_cap}× per property per day` : ''}`} />
                    ))}
                </div>
            </Panel>
            <Panel title="Decay, thresholds and rate limit">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6">
                    <Input type="number" label="Half-life (days)" min="1" max="90" step="0.5" value={s.half_life_days} onChange={(e) => set('half_life_days')(e.target.value)} disabled={ro}
                        hint="A point earned this long ago counts half." />
                    <Input type="number" label="Look-back window (days)" min="7" max="365" value={s.window_days} onChange={(e) => set('window_days')(e.target.value)} disabled={ro} />
                    <Input type="number" label="Page views counted per property per day" min="1" max="20" value={s.daily_view_cap} onChange={(e) => set('daily_view_cap')(e.target.value)} disabled={ro} />
                    <Input type="number" label="Warm at (points)" min="1" step="0.5" value={s.warm_threshold} onChange={(e) => set('warm_threshold')(e.target.value)} disabled={ro} />
                    <Input type="number" label="Hot at (points)" min="1" step="0.5" value={s.hot_threshold} onChange={(e) => set('hot_threshold')(e.target.value)} disabled={ro} />
                    <Input type="number" label="Hours between alerts for one person" min="1" max="720" value={s.alert_cooldown_hours} onChange={(e) => set('alert_cooldown_hours')(e.target.value)} disabled={ro} />
                    <Input type="number" label="Days a sent share link can identify its recipient" min="1" max="60" value={s.share_link_days ?? 7} onChange={(e) => set('share_link_days')(e.target.value)} disabled={ro}
                        hint="Only the first browser to open the link, and only if nobody is signed in there. A forwarded link never identifies anyone." />
                </div>
            </Panel>
            {!ro && (
                <div className="flex gap-2">
                    <Button disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</Button>
                    <Button type="button" variant="ghost" onClick={resetDefaults}>Fill in the defaults</Button>
                </div>
            )}
        </form>
    );
};

const HotLeadsPage = () => {
    const api = useApiClient();
    const [params, setParams] = useSearchParams();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');
    const tab = params.get('tab') === 'settings' ? 'settings' : 'list';
    useEffect(() => { api.get('/api/hot-leads/meta').then(setMeta).catch((e) => setError(e.message)); }, [api]);
    if (error) return <div className="p-8"><Notice message={error} /></div>;
    if (!meta) return <Loading />;
    return (
        <div className="p-4 md:p-8">
            <PageHeader title="Hot leads"
                subtitle="Website behaviour — property views, return visits, favourites, shares, inquiries, saved searches — scored with points that fade over time. Linked to a person only after they identify themselves." />
            <Tabs tabs={[{ key: 'list', label: 'Hot leads' }, { key: 'settings', label: 'Settings' }]} active={tab}
                onChange={(k) => setParams(k === 'settings' ? { tab: 'settings' } : {})} />
            {tab === 'list' ? <LeadList seesEveryone={meta.sees_everyone} /> : <SettingsForm meta={meta} />}
        </div>
    );
};

export default HotLeadsPage;
